import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuCheck, LuChevronDown, LuLoaderCircle } from "react-icons/lu";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { colors, typography, spacing, effects, layout } from "../../../design/tokens";
import { SubscriptionProduct } from "../../../model/subscriptionProduct";

interface SubscriptionsModalItemProps {
  product: SubscriptionProduct;
  isDisabled: boolean;
  isLoading: boolean;
  onSelect: () => void;
  isPopular?: boolean;
  isScaleCard?: boolean;
  scaleOptions?: SubscriptionProduct[];
  selectedScaleIndex?: number;
  onScaleSelectionChange?: (index: number) => void;
  activeScaleIndex?: number | null;
}

export default function SubscriptionsModalItem({
  product,
  isDisabled,
  isLoading,
  onSelect,
  isPopular = false,
  isScaleCard = false,
  scaleOptions,
  selectedScaleIndex,
  onScaleSelectionChange,
  activeScaleIndex = null,
}: SubscriptionsModalItemProps) {
  const isCompactView = useIsCompactView();
  const isFreePlan = product.price === 0;
  const isActive = product.status === "active";

  // Determine if showing different tier and if it's upgrade/downgrade
  let scaleChangeType: 'upgrade' | 'downgrade' | null = null;
  if (isScaleCard && activeScaleIndex !== null && selectedScaleIndex !== undefined && selectedScaleIndex !== activeScaleIndex && scaleOptions) {
    const activePrice = scaleOptions[activeScaleIndex].price;
    const selectedPrice = scaleOptions[selectedScaleIndex].price;
    scaleChangeType = selectedPrice > activePrice ? 'upgrade' : 'downgrade';
  }

  // Compact static view for active free plan on mobile
  if (isCompactView && isActive && isFreePlan) {
    return (
      <div
        className={`w-full px-4 py-3
          ${colors.surface.secondary}
          ${effects.borderRadius.xl}
          border ${colors.border.primary}
          ${colors.text.primary}`}
      >
        <div className={layout.flex.between}>
          <span className={`${typography.size.lg} ${typography.weight.semibold}`}>{product.name}</span>
          <span className={`${typography.size.sm} ${typography.weight.medium} ${colors.text.secondary}`}>Active</span>
        </div>
        <div className={`${layout.flex.between} mt-1`}>
          <span className={`${typography.size.sm} ${typography.weight.semibold} ${colors.text.primary}`}>$0</span>
          <span className={`${typography.size.sm} ${colors.text.secondary}`}>
            {product.marketing_features?.[0]}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      key={product.name}
      className={`h-full max-w-[280px] w-full ${isCompactView && "max-w-none"}
        relative
        ${colors.surface.secondary}
        flex flex-col
        ${effects.borderRadius.xl}
        border ${colors.border.primary}
        ${effects.shadow.sm}`}
    >

      <div
        className={`w-full h-full px-5 py-5
        flex flex-col
        ${colors.text.primary} relative ${layout.zIndex.base}`}
      >
        {/* Plan Name */}
        <div className={`${layout.flex.between} mb-2`}>
          <span className={`${typography.size.base} ${typography.weight.semibold} tracking-tight ${colors.text.secondary}`}>
            {product.name}
          </span>
          {isPopular && (
            <span
              className={`${typography.size.xs} ${typography.weight.medium}
                px-2 py-0.5 ${effects.borderRadius.default}
                bg-white/10 border border-white/20 text-white/80`}
            >
              POPULAR
            </span>
          )}
        </div>

        {/* Price */}
        <Price price={product.price} currency={product.currency} />

        {/* Scale Dropdown */}
        {isScaleCard && scaleOptions && onScaleSelectionChange && (
          <ScaleDropdown
            options={scaleOptions}
            selectedIndex={selectedScaleIndex ?? 0}
            onChange={onScaleSelectionChange}
          />
        )}

        {/* Features List */}
        <FeaturesList features={product.marketing_features || []} />

        {/* CTA Button */}
        <div className={`mt-auto ${isScaleCard ? "pt-6" : "pt-3"}`}>
          <BottomPart
            buttonId={`subscriptions-modal-item-${product.name
              .toLowerCase()
              .replace(/ /g, "-")}`}
            status={product.status}
            onClick={onSelect}
            isDisabled={isDisabled}
            isLoading={isLoading}
            scaleChangeType={scaleChangeType}
          />
        </div>
      </div>
    </div>
  );
}

function Price({ price, currency }: { price: number; currency: string }) {
  const formatCurrency = (amount: number, currencyCode: string) => {
    try {
      // Convert amount from cents to dollars (divide by 100)
      const amountInCurrency = amount / 100;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyCode.toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amountInCurrency);
    } catch (error) {
      return `${currencyCode.toUpperCase()}${amount / 100}`;
    }
  };

  return (
    <div className="mb-3 flex items-center gap-2">
      <span className={`${typography.size["3xl"]} ${typography.weight.bold} leading-none tracking-tight`}>
        {formatCurrency(price, currency)}
      </span>
      {price > 0 && (
        <div className="flex flex-col justify-center">
          <span className={`${typography.size.xs} ${colors.text.secondary} leading-tight`}>per month</span>
          <span className={`${typography.size.xs} ${colors.text.placeholder} leading-tight`}>billed monthly</span>
        </div>
      )}
    </div>
  );
}

function FeaturesList({ features }: { features: string[] }) {
  return (
    <div className="mt-3">
      <span className={`${typography.size.sm} ${typography.weight.medium} ${colors.text.secondary}`}>
        You get:
      </span>
      <ul className={`flex flex-col ${spacing.gap.xs} mt-2`}>
        {features.map((feature, idx) => (
          <li key={idx} className={`flex items-start gap-2 ${typography.size.sm} ${colors.text.secondary}`}>
            <LuCheck size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ScaleDropdown({
  options,
  selectedIndex,
  onChange,
}: {
  options: SubscriptionProduct[];
  selectedIndex: number;
  onChange: (index: number) => void;
}) {
  const isCompactView = useIsCompactView();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0, maxHeight: 400 });

  // Desktop: Calculate menu position when opening
  useEffect(() => {
    if (isOpen && triggerRef.current && !isCompactView) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const bottomPadding = 16;
      const menuTop = rect.bottom + 4;
      const maxHeight = viewportHeight - menuTop - bottomPadding;
      setMenuPosition({
        top: menuTop,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(maxHeight, 100), // Minimum 100px
      });
    }
  }, [isOpen, isCompactView]);

  // Outside click detection for desktop dropdown
  useEffect(() => {
    if (isCompactView) return; // Mobile uses backdrop click instead

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedTrigger = triggerRef.current?.contains(target);
      const clickedMenu = menuRef.current?.contains(target);
      if (!clickedTrigger && !clickedMenu) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCompactView]);

  const selectedOption = options[selectedIndex];

  // Shared menu option renderer
  const renderOption = (option: SubscriptionProduct, idx: number) => (
    <button
      key={option.id}
      type="button"
      role="menuitem"
      onClick={() => {
        onChange(idx);
        setIsOpen(false);
      }}
      className={`w-full px-4 py-3 text-left ${typography.size.sm}
        hover:bg-white/10 transition-colors
        ${idx === selectedIndex
          ? `${colors.text.primary} ${typography.weight.medium} bg-white/5`
          : colors.text.secondary
        }`}
    >
      {option.marketing_features?.[0]?.replace(" per month", " / month") || option.name}
    </button>
  );

  // Desktop: Positioned dropdown via portal
  const desktopDropdown = !isCompactView && isOpen && createPortal(
    <div
      ref={menuRef}
      className={`fixed z-[9999]
        ${colors.surface.primary}
        border ${colors.border.primary}
        ${effects.borderRadius.lg} ${effects.shadow.lg} overflow-y-auto`}
      style={{
        top: menuPosition.top,
        left: menuPosition.left,
        width: menuPosition.width,
        maxHeight: menuPosition.maxHeight,
        backgroundColor: colors.hex.surfacePrimary,
      }}
      role="menu"
    >
      {options.map(renderOption)}
    </div>,
    document.body
  );

  // Mobile: Bottom sheet via portal
  const mobileBottomSheet = isCompactView && isOpen && createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[100]"
        onClick={() => setIsOpen(false)}
      />
      {/* Sheet */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-[101]
          ${colors.surface.primary}
          rounded-t-2xl max-h-[70vh] overflow-y-auto
          animate-[slideUp_0.2s_ease-out]`}
        style={{
          animation: 'slideUp 0.2s ease-out',
        }}
      >
        {/* Header */}
        <div className={`sticky top-0 px-4 py-3 border-b ${colors.border.primary}
          flex justify-between items-center ${colors.surface.primary}`}>
          <span className={`${typography.weight.semibold} ${colors.text.primary}`}>
            Select token amount
          </span>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className={`${colors.text.secondary} p-1`}
          >
            <LuChevronDown size={20} className="rotate-180" />
          </button>
        </div>
        {/* Options */}
        <div className="py-2" role="menu">
          {options.map(renderOption)}
        </div>
      </div>
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>,
    document.body
  );

  return (
    <div className="w-full mb-2">
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-2.5 ${effects.borderRadius.lg}
          ${colors.surface.primary}
          border ${colors.border.primary}
          ${colors.text.primary} ${typography.size.sm} ${typography.weight.medium}
          ${layout.flex.between}
          hover:border-dark-text-secondary
          ${effects.transition.default} cursor-pointer`}
      >
        <span>{selectedOption.marketing_features?.[0]?.replace(" per month", " / month") || selectedOption.name}</span>
        <LuChevronDown
          size={18}
          className={`${colors.text.secondary} ${effects.transition.transform} ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Desktop dropdown or Mobile bottom sheet */}
      {desktopDropdown}
      {mobileBottomSheet}
    </div>
  );
}

function BottomPart({
  buttonId,
  status,
  isDisabled,
  isLoading,
  onClick,
  scaleChangeType = null,
}: {
  buttonId: string;
  status: string;
  isDisabled: boolean;
  isLoading: boolean;
  onClick: () => void;
  scaleChangeType?: 'upgrade' | 'downgrade' | null;
}) {
  // If scaleChangeType is set, show upgrade/downgrade button instead of active
  const showActiveStatus = status === "active" && scaleChangeType === null;

  // Determine button text
  let buttonText = status[0].toUpperCase() + status.slice(1);
  if (scaleChangeType === 'upgrade') {
    buttonText = 'Upgrade';
  } else if (scaleChangeType === 'downgrade') {
    buttonText = 'Downgrade';
  }

  return (
    <div className="w-full flex items-end justify-center">
      {showActiveStatus ? (
        <div
          className={`w-full h-[44px] ${effects.borderRadius.lg} ${typography.weight.semibold} ${typography.size.sm} ${typography.tracking.wider}
            ${colors.surface.primary} border ${colors.border.primary}
            ${colors.text.secondary}
            ${layout.flex.center}`}
        >
          Active
        </div>
      ) : (
        <button
          id={buttonId}
          className={`w-full h-[44px]
            bg-primary-blue text-white
            ${effects.borderRadius.lg} ${typography.weight.semibold} ${typography.size.sm} ${typography.tracking.wider}
            ${effects.transition.default}
            hover:opacity-90 ${effects.disabled} disabled:cursor-not-allowed
            ${layout.flex.center} gap-2`}
          onClick={onClick}
          disabled={isDisabled}
        >
          {isLoading ? (
            <LuLoaderCircle className="animate-spin" size={18} />
          ) : (
            buttonText
          )}
        </button>
      )}
    </div>
  );
}
